import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { Search, Plus, Link2, X } from "lucide-react";
import useRequest from "../../../hook/use-req";
import { CampaignData, CampaignContent } from "../../../types";
import { SingleCampaignResponse } from "../../../../views/marketing-pages/campaign/types";
import { LivePreviewPanel } from "./live-preview";
import ConvoAIPanel from "../convoai-hub";
import { CostEstimateResponse } from "./types";
import { TemplateCreator, TemplateModule } from "../../../../views/marketing-pages/templates";
import ScrollToTop from "../../../../routes/scroll-top";
import { apiErrorMessage } from "../../../../utils/function";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SmsContentCreationProps {
  campaignData: CampaignData;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  onBack: () => void;
}

// ─────────────────────────────────────────────────────────────
// VARIABLE MAPPING — same CSV Column / Static Value experience as WhatsApp
// (messaging.tsx) and RCS (rcs-content-creation.tsx). Kept as its own
// self-contained copy rather than a shared import, matching how RCS already
// duplicated this from WhatsApp rather than extracting a shared module.
//
// One real difference from WhatsApp/RCS: their variables come from picking a
// fixed template (a discrete, infrequent event), so they rebuild the mapping
// list from scratch on template change. SMS's message is free-typed, so
// re-deriving variableNames on every keystroke and blindly rebuilding
// mappings would wipe out whatever the user just configured for a variable
// that's still present.
//
// A second difference, since mapping here performs a literal substitution
// into the Body text (confirmed): once substituted, the {{variableName}}
// token is gone from the text, so it can no longer be re-derived by
// scanning for it. addNewVariableMappings() below therefore only ever ADDS
// rows for genuinely new tokens — it never drops a row just because its
// token disappeared, since that's expected once it's been mapped, and the
// user may still want to come back and change the mapping (a different CSV
// column, a different static value) or remove it via the row's own delete
// button. lastInsertedValue tracks what's currently sitting in the text for
// a variable so a later re-map can find-and-replace it correctly.
// ─────────────────────────────────────────────────────────────

type VariableSource = "csv" | "static";

interface VariableMapping {
  variableName: string;
  source: VariableSource;
  csvColumn?: string;
  staticValue?: string;
  // What's currently substituted into the Body text for this variable, if
  // anything — undefined until the first substitution happens.
  lastInsertedValue?: string;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractVariableNames(text: string): string[] {
  const matches = [...(text || "").matchAll(/{{(\w+)}}/g)];
  const unique = new Map<string, true>();
  matches.forEach((m) => unique.set(m[1], true));
  return [...unique.keys()];
}

function addNewVariableMappings(
  names: string[],
  prevMappings: VariableMapping[],
  csvColumns: string[],
  bodyText: string,
  existingColumnMap?: Record<string, string>,
  existingStatic?: Record<string, string>,
): VariableMapping[] {
  const existingNames = new Set(prevMappings.map((m) => m.variableName));
  const newOnes = names
    .filter((name) => !existingNames.has(name))
    .map((name): VariableMapping => {
      // If the real {{name}} token is already gone from the text but the
      // seed's own literal word is still sitting there (a template whose
      // {{n}} got destroyed by the now-reverted substitution bug — e.g.
      // "muyi" typed straight into the body where {{1}} used to be), track
      // that word as already "inserted" so picking a new value has
      // something to find-and-replace. Otherwise there'd be nothing in the
      // text a fresh mapping could ever substitute.
      const hasToken = bodyText.includes(`{{${name}}}`);
      const seedValue = existingStatic?.[name];
      const recoveredValue = !hasToken && seedValue && bodyText.includes(seedValue) ? seedValue : undefined;

      if (existingColumnMap?.[name]) {
        return {
          variableName: name,
          source: "csv",
          csvColumn: existingColumnMap[name],
          ...(recoveredValue ? { lastInsertedValue: recoveredValue } : {}),
        };
      }
      if (seedValue) {
        return {
          variableName: name,
          source: "static",
          staticValue: seedValue,
          ...(recoveredValue ? { lastInsertedValue: recoveredValue } : {}),
        };
      }

      const autoMatch = csvColumns.find((col) =>
        col.toLowerCase() === name.toLowerCase() ||
        col.toLowerCase().replace(/[_\s]/g, "") === name.toLowerCase().replace(/[_\s]/g, "")
      );
      if (autoMatch && csvColumns.length > 0) {
        return { variableName: name, source: "csv", csvColumn: autoMatch };
      }

      return { variableName: name, source: "static", staticValue: "" };
    });

  // Same array reference when nothing new, so the effect that depends on
  // this doesn't re-fire for no reason.
  return newOnes.length > 0 ? [...prevMappings, ...newOnes] : prevMappings;
}

// Shared by both preview surfaces below — a mapped variable resolves to its
// real value (CSV column's example row, or the static value); returns null
// when there's nothing to show yet (unmapped, or mapped but empty).
function resolveVariableValue(name: string, mappings: VariableMapping[]): string | null {
  const mapping = mappings.find((m) => m.variableName === name);
  if (!mapping) return null;

  if (mapping.source === "static") {
    return mapping.staticValue || null;
  }

  if (mapping.source === "csv" && mapping.csvColumn) {
    const savedHeadersStr = localStorage.getItem("csv_headers");
    const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
    const savedRowStr = localStorage.getItem("first_row_values");
    const csvColumnsRow: (string | number | null)[] = savedRowStr ? JSON.parse(savedRowStr) : [];
    const colIndex = csvColumns.indexOf(mapping.csvColumn);
    const rowValue = colIndex !== -1 ? csvColumnsRow[colIndex] : undefined;
    return rowValue !== undefined && rowValue !== null ? String(rowValue) : null;
  }

  return null;
}

// For display only (phone mockup / cost estimate) — never used for the
// editable Body textarea, which must keep showing the raw {{variable}}
// template (it's still what actually gets sent + personalized server-side
// per contact). A mapped variable resolves with the braces removed entirely;
// an unmapped one collapses to bare "{{}}" rather than showing the variable
// name, per the requested "curly braces only when there's no value yet".
function resolvePreviewText(text: string, mappings: VariableMapping[]): string {
  if (!text) return text;
  return text.replace(/{{(\w+)}}/g, (_full, name) => resolveVariableValue(name, mappings) ?? "{{}}");
}

function mappingsToPayload(mappings: VariableMapping[]): {
  variable_column_map: Record<string, string>;
  variables: Record<string, string>;
  data_to_personalize: string[];
} {
  const variable_column_map: Record<string, string> = {};
  const variables: Record<string, string> = {};
  const data_to_personalize: string[] = [];

  mappings.forEach((m) => {
    if (m.source === "csv" && m.csvColumn) {
      variable_column_map[m.variableName] = m.csvColumn;
      data_to_personalize.push(m.variableName);
    } else {
      variables[m.variableName] = m.staticValue || "";
    }
  });

  return { variable_column_map, variables, data_to_personalize };
}

interface VariableMappingFormProps {
  mappings: VariableMapping[];
  onChange: (mappings: VariableMapping[]) => void;
}

function VariableMappingForm({ mappings, onChange }: VariableMappingFormProps) {
  if (mappings.length === 0) return null;

  const removeMapping = (index: number) => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  // Headers/preview row come from CSV upload (campaign-details/upload-tab.tsx),
  // same localStorage keys WhatsApp/RCS already read.
  const savedHeadersStr = localStorage.getItem("csv_headers");
  const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
  const savedRowStr = localStorage.getItem("first_row_values");
  const csvColumnsRow: (string | number | null)[] = savedRowStr ? JSON.parse(savedRowStr) : [];
  const savedMetadataStr = localStorage.getItem("metadata");
  const metadata: { unique_file_name?: string } | null = savedMetadataStr ? JSON.parse(savedMetadataStr) : null;

  const mappedCount = mappings.filter(
    (m) => (m.source === "csv" && m.csvColumn) || (m.source === "static" && m.staticValue)
  ).length;

  const updateMapping = (index: number, patch: Partial<VariableMapping>) => {
    onChange(mappings.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const getPreview = (m: VariableMapping): string => {
    if (m.source === "csv" && m.csvColumn) {
      const colIndex = csvColumns.indexOf(m.csvColumn);
      const rowValue = colIndex !== -1 ? csvColumnsRow[colIndex] : undefined;
      return rowValue !== undefined && rowValue !== null ? String(rowValue) : m.csvColumn;
    }
    return m.staticValue || "";
  };

  return (
    <div>
      {metadata?.unique_file_name && (
        <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-2 truncate">{metadata.unique_file_name}</p>
      )}
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">List of message body variables</p>
        <span className="text-xs font-medium px-3 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full">
          {mappedCount}/{mappings.length} Mapped
        </span>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[100px_1fr_120px_28px] gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <div>Variable</div>
          <div>Map Data Source</div>
          <div>Column Preview</div>
          <div />
        </div>

        {mappings.map((m, idx) => (
          <div key={m.variableName} className="grid grid-cols-[100px_1fr_120px_28px] gap-3 px-4 py-3.5 border-b last:border-b-0 border-gray-100 dark:border-gray-800 items-center">
            <div className="font-mono text-xs text-indigo-600 dark:text-indigo-400 break-all">
              {`{{${m.variableName}}}`}
            </div>

            <div className="space-y-2">
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={m.source === "csv"}
                    onChange={() => updateMapping(idx, { source: "csv", staticValue: undefined })}
                    className="accent-indigo-600"
                  />
                  <span className="text-xs">CSV Column</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={m.source === "static"}
                    onChange={() => updateMapping(idx, { source: "static", csvColumn: undefined })}
                    className="accent-indigo-600"
                  />
                  <span className="text-xs">Static Value</span>
                </label>
              </div>

              {m.source === "csv" ? (
                <select
                  value={m.csvColumn || ""}
                  onChange={(e) => updateMapping(idx, { csvColumn: e.target.value })}
                  className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="">Select column...</option>
                  {csvColumns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              ) : (
                <input
                  value={m.staticValue || ""}
                  onChange={(e) => updateMapping(idx, { staticValue: e.target.value })}
                  placeholder="Enter static value"
                  className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              )}
            </div>

            <div>
              {getPreview(m) ? (
                <span className="inline-block bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 px-2 py-1 rounded text-xs truncate max-w-full">
                  {getPreview(m)}
                </span>
              ) : (
                <span className="text-gray-300 dark:text-gray-600 italic text-xs">—</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => removeMapping(idx)}
              title="Remove this variable mapping"
              className="text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Right Panel Component (like in Messaging) ────────────────────────────────

interface RightPanelProps {
  messageText: string;
  senderName?: string;
  isConvoOpen: boolean;
  onToggleConvo: () => void;
  onConvoSend: (message: string, history: Array<{ role: "user" | "ai"; text: string }>) => Promise<string>;
  costEstimate: CostEstimateResponse | null;
  smsSegments: number;
  channel: string;
}

function RightPanel({
  messageText,
  senderName,
  isConvoOpen,
  onToggleConvo,
  onConvoSend,
  costEstimate,
  smsSegments,
  channel
}: RightPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  return (
    <div className="flex flex-col h-full w-full lg:w-[320px] bg-white dark:bg-gray-900 lg:border-l border-slate-200 dark:border-slate-700">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b bg-[#F7FCF3] dark:bg-emerald-500/10 border-slate-100 dark:border-slate-800 shrink-0">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Live Preview</span>
        <button
          onClick={onToggleConvo}
          title={isConvoOpen ? "Back to preview" : "Open ConvoAI"}
          className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors
            ${isConvoOpen
              ? "bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"}`}
        >
          {isConvoOpen ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {/* Live Preview Panel */}
        <div className={`absolute inset-0 flex flex-col justify-start pt-6 pb-4 px-4
          overflow-y-auto transition-opacity duration-200
          ${isConvoOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <LivePreviewPanel
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(prev => !prev)}
            senderName={senderName}
            messageText={messageText}
            costEstimate={costEstimate}
            smsSegments={smsSegments}
            channel={channel}
          />
          <button
            onClick={onToggleConvo}
            className="mt-5 w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg
              hover:bg-indigo-700 transition-colors"
            title="Open ConvoAI"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 4V14H4a2 2 0 01-2-2V5z" />
            </svg>
          </button>
        </div>

        {/* ConvoAI Panel */}
        <div className={`absolute inset-0 transition-opacity duration-200
          ${isConvoOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ConvoAIPanel
            isOpen={isConvoOpen}
            onClose={onToggleConvo}
            onSend={onConvoSend}
            placeholder="Ask anything..."
            greeting="I can help you craft the perfect SMS message. Ask me to adjust the tone, add emojis, shorten the text, or anything else!"
            quickActions={["Adjust the Tone", "Change promo", "Edit URL", "Add Emojis", "Make it shorter", "Make it more formal"]}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export const SmsContentCreation: React.FC<SmsContentCreationProps> = ({
  campaignData,
  singleCampaign,
  setCampaignData,
  onSubmit,
  onValidation,
  onBack,
}) => {
  const token = localStorage.getItem("authToken");
  const [messageText, setMessageText] = useState<string>(campaignData?.messages || "");
  const [extractedUrl, setExtractedUrl] = useState<string>("");
  const [shortenedUrl, setShortenedUrl] = useState<string>("");
  const [enableLinkShortening, setEnableLinkShortening] = useState<boolean>(false);
  // The original, human-typed destination URL that shortenedUrl was
  // generated from. Needed as actual state (not just a ref) since the
  // "Full:" URL display below must keep showing this — once the shortened
  // link is substituted into the Body text, extractedUrl (re-derived from
  // that text) becomes the short link too, so it can no longer be used for
  // "Full". Also what toggling shortening back off restores into the text.
  const [originalUrl, setOriginalUrl] = useState<string>("");
  const [isLinkValid, setIsLinkValid] = useState(true);
  const [isMessageTextValid, setIsMessageTextValid] = useState(!!campaignData?.messages);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isConvoOpen, setIsConvoOpen] = useState(false);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [, setConvoHistory] = useState<Array<{ role: "user" | "ai"; text: string }>>([]);
  const audienceId = localStorage.getItem("audienceId");
  const campaignId = localStorage.getItem("campaignId");
  const [showCreator, setShowCreator] = React.useState(false);
  const [costEstimate, setCostEstimate] = useState<CostEstimateResponse | null>(null);
  const [variableMappings, setVariableMappings] = useState<VariableMapping[]>([]);
  // Numbered {{n}} tokens are meaningless on their own — a selected
  // template's own declared variables ({name, index, example}) are the only
  // thing that says what {{1}}/{{2}} actually mean, so hydrate the mapping
  // rows' default static values from them (keyed by index-as-string, same
  // as the {{n}} token itself) as soon as a template is picked.
  const [templateVariableSeed, setTemplateVariableSeed] = useState<Record<string, string>>({});
  const { makeRequest: getEstimate } = useRequest(`/campaign/estimate_cost`, "GET",
    { Authorization: `Bearer ${token}` }
  );
  const [templates, setTemplates] = useState<TemplateModule[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const { makeRequest: getTemplates } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: shortLinkRequest, loading } = useRequest(
    `/campaign/track?url_link=${encodeURIComponent(extractedUrl)}`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
    setValue,
  } = useForm({
    defaultValues: { ...campaignData, messages: campaignData?.messages || "" },
    mode: "onChange",
  });

  const watchedValues = watch();

  // Helpers
  const extractUrlFromText = (text: string): string => {
    const urls = text?.match(/(https?:\/\/[^\s]+)/g);
    return urls?.[0] ?? "";
  };

  const removeUrlFromText = (text: string): string =>
    text?.replace(/(https?:\/\/[^\s]+)/g, "").trim();

  const removeEmojis = (text: string) =>
    text?.replace(/([\u2600-\u27BF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g, "");

  const isValidUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const u = new URL(url);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  // A shortened link is always billed/counted as this many characters,
  // regardless of the actual returned short-URL's length (which varies with
  // the domain and any {MERGE_FIELD} placeholders in it, e.g.
  // "https://go.dalmlabs.com/9Zl/{AUDIENCE_ID}" is 42 chars but still only
  // counts as 32) — the message actually sent still contains the real URL,
  // this only affects the segment/character estimate shown here.
  const SHORTENED_LINK_CHAR_COUNT = 32;

  const calculateSMSSegments = (text: string) => {
    const textWithoutUrl = removeUrlFromText(text);
    const urlLen = enableLinkShortening && shortenedUrl
      ? SHORTENED_LINK_CHAR_COUNT
      : extractedUrl?.length;
    const totalChars = textWithoutUrl?.length + (urlLen || 0);
    const segments = Math.ceil(totalChars / 160) || 1;
    return {
      segments,
      availableChars: segments * 160 - totalChars,
      textOnlyCount: textWithoutUrl?.length,
      totalChars,
    };
  };

  const { segments, totalChars } = calculateSMSSegments(messageText);
  const smsMessageUnit = Math.max(segments, campaignData.smsSegment || 1, 1);

  // Fetch cost estimate when campaignId or message/sms segments change
  useEffect(() => {
    const fetchData = async () => {
      if (!campaignId) return;

      const campaignPayload = {
        campaign_id: campaignId,
        channel: campaignData?.campaign_channel,
        message: previewText || campaignData?.messages,
        audience_ids: audienceId || campaignData?.audienceId,
        sms_segments: smsMessageUnit
      };

      const [response] = await getEstimate(undefined, campaignPayload);
      if (response) {
        setCostEstimate(response);
      }
    };

    fetchData();
  }, [campaignId, campaignData?.messages, campaignData?.audienceId, smsMessageUnit]);

  useEffect(() => {
    setCampaignData((prev) => {
      const updated = {
        ...prev,
        ...watchedValues,
        smsSegment: prev.smsSegment || 1,
      };

      if (JSON.stringify(prev) === JSON.stringify(updated)) {
        return prev;
      }

      return updated;
    });
  }, [watchedValues]);

  useEffect(() => {
    const { segments: s } = calculateSMSSegments(messageText);
    setCampaignData((prev) => ({
      ...prev,
      messages: messageText,
      link: enableLinkShortening && shortenedUrl ? shortenedUrl : extractedUrl,
      smsSegment: Math.max(s, 1),
    }));
  }, [messageText, extractedUrl, shortenedUrl, enableLinkShortening]);

  useEffect(() => {
    setIsMessageTextValid(messageText?.length > 0);
  }, [messageText]);

  useEffect(() => {
    setIsLinkValid(extractedUrl ? isValidUrl(extractedUrl) : true);
    // The URL sitting in the text changed to something that's neither the
    // link we already shortened nor the shortened result itself (the user
    // edited/replaced it) — drop the stale shortened link so a fresh one
    // gets generated for the new URL instead of silently going unused.
    if (extractedUrl && shortenedUrl && extractedUrl !== shortenedUrl && extractedUrl !== originalUrl) {
      setShortenedUrl("");
      setOriginalUrl("");
    }
    if (enableLinkShortening && extractedUrl && !shortenedUrl) handleShortenUrl(extractedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedUrl, enableLinkShortening, shortenedUrl]);

  useEffect(() => {
    const url = extractUrlFromText(messageText);
    setExtractedUrl(url);
  }, [messageText]);

  useEffect(() => {
    onValidation(isValid && isMessageTextValid && isLinkValid);
  }, [isValid, isMessageTextValid, isLinkValid, onValidation]);

  // Re-derive {{variable}} tokens from the typed message and add rows for
  // any genuinely new ones — see addNewVariableMappings' comment above for
  // why this never drops an existing row on its own.
  useEffect(() => {
    const textNames = extractVariableNames(messageText);
    const savedHeadersStr = localStorage.getItem("csv_headers");
    const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
    const existingColumnMap = (singleCampaign?.content as any)?.variable_column_map || campaignData.content?.variable_column_map || {};
    // The campaign's own already-saved values (editing an existing send)
    // win over the template's generic defaults; the template seed only
    // fills in what hasn't been customized yet.
    const existingStatic = {
      ...templateVariableSeed,
      ...((singleCampaign?.content as any)?.variables || campaignData.content?.variables || {}),
    };
    // A template saved while the (now-reverted) literal-substitution
    // behavior was live can declare a variable whose {{n}} token no longer
    // exists anywhere in its content — scanning the live text alone would
    // silently drop it. Union in whatever names the metadata already knows
    // about too, so the row (and its data) still shows up.
    const names = Array.from(new Set([...textNames, ...Object.keys(existingStatic), ...Object.keys(existingColumnMap)]));
    setVariableMappings((prev) => addNewVariableMappings(names, prev, csvColumns, messageText, existingColumnMap, existingStatic));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageText, templateVariableSeed]);

  // Once a mapping resolves to a real value (CSV column picked, or a static
  // value typed), permanently substitute it into the actual Body text —
  // confirmed as a deliberate, literal replacement: the outgoing message
  // becomes that literal text, so per-contact CSV personalization no longer
  // applies to that specific variable once it's been substituted this way.
  // The row itself stays in the list (per feedback: the user may still want
  // to change the mapping) — lastInsertedValue lets a later re-map find the
  // previously-substituted text and swap in the new value, since the
  // original {{variable}} token is gone after the first substitution.
  useEffect(() => {
    let updated = messageText;
    let mappingsChanged = false;
    const nextMappings = variableMappings.map((m) => {
      const value = resolveVariableValue(m.variableName, variableMappings);
      if (!value || value === m.lastInsertedValue) return m;

      const bracketToken = `{{${m.variableName}}}`;
      const target = m.lastInsertedValue && updated.includes(m.lastInsertedValue)
        ? m.lastInsertedValue
        : bracketToken;

      let nextText: string | null = null;
      if (target === bracketToken) {
        if (updated.includes(target)) nextText = updated.split(target).join(value);
      } else {
        // Recovering from an already-corrupted template: `target` here is a
        // plain word (e.g. "work"), not a {{n}} placeholder — match on word
        // boundaries so this doesn't also clobber it inside an unrelated
        // word (e.g. the "work" baked into "howork", from the original
        // {{2}} having been substituted directly against "how" with no
        // space typed before it).
        const re = new RegExp(`\\b${escapeRegExp(target)}\\b`, "g");
        if (re.test(updated)) nextText = updated.replace(re, value);
      }

      if (nextText === null) return m;
      updated = nextText;
      mappingsChanged = true;
      return { ...m, lastInsertedValue: value };
    });

    if (updated !== messageText) {
      setMessageText(updated);
      setValue("messages", updated, { shouldValidate: true });
    }
    if (mappingsChanged) {
      setVariableMappings(nextMappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableMappings]);

  // Keep campaignData.content in sync so shedule-launch.tsx's "WhatsApp /
  // SMS content" branch (which already reads content.variable_column_map /
  // .variables / .data_to_personalize / .personalize generically) picks up
  // whatever's mapped here — same wiring WhatsApp/RCS already rely on.
  useEffect(() => {
    const { variable_column_map, variables, data_to_personalize } = mappingsToPayload(variableMappings);
    setCampaignData((prev) => ({
      ...prev,
      content: {
        template_id: prev.content?.template_id,
        template_name: prev.content?.template_name,
        variables,
        variable_column_map,
        data_to_personalize,
        personalize: data_to_personalize.length > 0,
        header_type: "none",
        header_value: undefined,
        message: messageText,
        footer: "",
        buttons: [],
      } as CampaignContent,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableMappings]);

  useEffect(() => {
    if (singleCampaign?.content?.message) {
      const msg = singleCampaign.content.message;
      const url = singleCampaign.content.url || "";

      setMessageText(msg);
      setValue("messages", msg, { shouldValidate: true });

      if (url) {
        setExtractedUrl(url);
        setShortenedUrl("");
        setEnableLinkShortening(false);
        setOriginalUrl("");
      }
    }
  }, [singleCampaign]);

  const handleShortenUrl = async (url?: string) => {
    const urlToShorten = url || extractedUrl;
    if (!urlToShorten) return;

    const [response] = await shortLinkRequest();
    if (response?.status) {
      const shortened = response.shortened_url;
      setShortenedUrl(shortened);
      setOriginalUrl(urlToShorten);
      // The shortened link is what actually gets sent — substitute it
      // straight into the Body text (same literal-substitution approach
      // already used for resolved {{variable}} tokens above). Previously
      // this only fed the Live Preview/character count, while the editable
      // text — and the message actually saved/submitted — kept the long
      // destination URL.
      const updatedText = messageText.includes(urlToShorten) ? messageText.replace(urlToShorten, shortened) : messageText;
      if (updatedText !== messageText) {
        setMessageText(updatedText);
        setValue("messages", updatedText, { shouldValidate: true });
      }
      toast.success(response.message || "Link shortened successfully!");
    } else {
      toast.error(apiErrorMessage(response, "Failed to generate!"));
    }
  };

  const handleToggleShortening = async (enabled: boolean) => {
    setEnableLinkShortening(enabled);
    if (enabled) {
      if (extractedUrl && !shortenedUrl) {
        await handleShortenUrl();
      } else if (shortenedUrl && originalUrl && messageText.includes(originalUrl)) {
        // Re-enabling after having toggled off — the long URL is back in
        // the text (see the "else" branch below); swap the already-known
        // shortened link back in instead of generating a new one.
        const updatedText = messageText.replace(originalUrl, shortenedUrl);
        setMessageText(updatedText);
        setValue("messages", updatedText, { shouldValidate: true });
      }
    } else if (shortenedUrl && originalUrl && messageText.includes(shortenedUrl)) {
      // Restore the original destination URL when shortening is turned off.
      const updatedText = messageText.replace(shortenedUrl, originalUrl);
      setMessageText(updatedText);
      setValue("messages", updatedText, { shouldValidate: true });
    }
  };

  const handleSelectTemplate = (template: TemplateModule) => {
    setSelectedTemplateId(template.id);
    // Remove any existing URLs from template content
    const sanitized = removeEmojis(template.content);
    setMessageText(sanitized);
    setValue("messages", sanitized, { shouldValidate: true });

    // {{1}}/{{2}} mean nothing without the template's own variables array —
    // seed each row's default static value from it (see the reconcile
    // effect above), keyed by index-as-string to match the {{n}} token.
    const seed: Record<string, string> = {};
    (template.variables || []).forEach((v) => {
      if (v?.example) seed[String(v.index)] = v.example;
    });
    setTemplateVariableSeed(seed);
  };

  const handleFormSubmit = (data: any) => {
    if (!data.messages?.trim()) { toast.error("Message field is required"); return; }
    if (extractedUrl && !isValidUrl(extractedUrl)) { toast.error("Please enter a valid URL"); return; }
    const { variable_column_map, variables, data_to_personalize } = mappingsToPayload(variableMappings);
    onSubmit({
      messages: data.messages,
      link: enableLinkShortening && shortenedUrl ? shortenedUrl : extractedUrl,
      smsSegment: campaignData.smsSegment,
      content: {
        template_id: campaignData.content?.template_id,
        template_name: campaignData.content?.template_name,
        variables,
        variable_column_map,
        data_to_personalize,
        personalize: data_to_personalize.length > 0,
        header_type: "none",
        header_value: undefined,
        message: data.messages,
        footer: "",
        buttons: [],
      },
    });
  };

  const handleConvoSend = async (message: string, history: Array<{ role: "user" | "ai"; text: string }>): Promise<string> => {
    setConvoHistory(history);

    await new Promise(resolve => setTimeout(resolve, 800));

    const lowerMessage = message.toLowerCase();
    let newMessageText = "";
    let response = "";

    if (lowerMessage.includes("adjust the tone")) {
      newMessageText = "Hey there! 🎉 Great news! Our amazing sale is here. Grab 10% off with code SALE120. Shop now!";
      response = `I've adjusted the tone. Here's your updated message:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("change promo")) {
      newMessageText = "Limited time offer! 🚀 Get 20% off your next purchase with code FLASH20. Don't miss out!";
      response = `I've updated the promo. New message:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("add emoji")) {
      newMessageText = "Hello user! 😊 Our sale is here! 🎉 Get 10% off with code SALE120 💰 Shop today! 🛍️";
      response = `I've added some emojis:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("make it shorter")) {
      newMessageText = "Sale alert! 10% off with SALE120. Shop now!";
      response = `Here's a shorter version:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("make it more formal")) {
      newMessageText = "Dear valued customer, enjoy 10% savings using code SALE120. Visit our store today.";
      response = `Here's a more formal version:\n\n"${newMessageText}"`;
    }
    else if (lowerMessage.includes("edit url")) {
      response = "I've extracted the URL from your message. You can edit it in the Destination URL field above.";
    }
    else {
      response = "I've processed your request. Let me know if you need any other adjustments!";
    }

    if (newMessageText) {
      setMessageText(newMessageText);
      setValue("messages", newMessageText, { shouldValidate: true });
    }

    return response;
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    const [response] = await getTemplates(undefined, { channel: 'SMS' });
    // Disabled templates can't be used in a campaign.
    const usable = (list: any[]) => list.filter((t) => t?.is_active !== false);
    if (response && response.data) {
      setTemplates(usable(response.data));
    } else if (response && Array.isArray(response)) {
      setTemplates(usable(response));
    } else {
      setTemplates([]);
    }
    setLoadingTemplates(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter((t) =>
    t.template_name.toLowerCase().includes(templateSearch.toLowerCase())
  );

  const urlAdjustedText = enableLinkShortening && shortenedUrl && extractedUrl
    ? messageText?.replace(extractedUrl, shortenedUrl)
    : messageText;
  // The Body textarea keeps showing raw {{variable}} placeholders (it's the
  // editable template) — only the preview/mockup/cost-estimate text below
  // resolves them to real values.
  const previewText = resolvePreviewText(urlAdjustedText || "", variableMappings);



  return (
    <div className="min-h-screen flex flex-col">
      {/* Page Header */}
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Choose Your SMS Template</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Pick a message template to edit or create a new one</p>
        </div>
        <button type="button" onClick={() => setShowCreator(true)} className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900 shadow-sm">
          <Plus className="w-4 h-4" />
          Create New Template
        </button>
      </div>

      {/* Main grid */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* ── Left: Template Sidebar ── */}
          <div className="w-full md:w-72 lg:w-80 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search Templates"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredTemplates?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No templates found</p>
                  <button
                    onClick={() => setShowCreator(true)}
                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Create your first template
                  </button>
                </div>
              ) : (
                filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`w-full text-left px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedTemplateId === template.id ? "bg-indigo-50 border-r-2 border-indigo-500" : ""
                      }`}
                  >
                    <p className={`text-sm font-semibold capitalize ${selectedTemplateId === template.id ? "text-indigo-700 dark:text-indigo-400" : "text-gray-900 dark:text-gray-100"}`}>
                      {template.template_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{template.content}</p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* ── Center: SMS Composer ── */}
          <form
            id="step-3-form"
            onSubmit={handleSubmit(handleFormSubmit)}
            className="flex-1 overflow-y-auto"
          >
            <div className="px-4 md:px-6 flex flex-col">
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm h-fit flex-1 flex flex-col">
                {/* Section header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 tracking-wide">SMS Message</h2>
                </div>

                <div className="p-6 flex-1 flex flex-col gap-5">
                  {/* Body label */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Body</label>
                    <Controller
                      name="messages"
                      control={control}
                      rules={{ required: "Message is required" }}
                      render={({ field, fieldState }) => (
                        <div className="relative">
                          <textarea
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              const noEmoji = removeEmojis(e.target.value);
                              field.onChange(noEmoji);
                              setMessageText(noEmoji);
                            }}
                            placeholder="Type your SMS message here..."
                            rows={7}
                            className={`w-full px-4 py-3 border rounded-lg text-sm resize-none bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent ${fieldState.error ? "border-red-300" : "border-gray-200 dark:border-gray-700"
                              }`}
                          />
                          {/* Character counter bottom bar */}
                          <div className="flex items-center justify-between mt-1.5 px-1">
                            <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                              {totalChars} / {smsMessageUnit * 160} | {smsMessageUnit} SMS
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setIsConvoOpen(true);
                                setIsMobilePreviewOpen(true);
                              }}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                              title="Open ConvoAI"
                            >
                              <Link2 className="w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                            </button>
                          </div>
                          {fieldState.error && (
                            <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Destination URL */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      {extractedUrl && (
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                          <span>Shorten URL</span>
                          <button
                            type="button"
                            onClick={() => handleToggleShortening(!enableLinkShortening)}
                            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${enableLinkShortening ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-700"
                              }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-900 transition-transform ${enableLinkShortening ? "translate-x-4" : "translate-x-0"
                                }`}
                            />
                          </button>
                        </label>
                      )}
                    </div>

                    {extractedUrl && (
                      <div className="flex flex-wrap gap-2">
                        <div className="px-3 py-2 rounded-md bg-[#EAEDFB] text-[#4B5563] text-sm">
                          <span className="font-medium text-[#6B7280]">Full:</span>{" "}
                          {/* extractedUrl is re-derived from the Body text, which now
                              holds the shortened link once shortening is applied —
                              so "Full" needs the original URL, not extractedUrl. */}
                          {enableLinkShortening && shortenedUrl && originalUrl ? originalUrl : extractedUrl}
                        </div>

                        {enableLinkShortening && shortenedUrl && (
                          <div className="px-3 py-2 rounded-md bg-[#EAEDFB] text-[#4B5563] text-sm">
                            <span className="font-medium text-[#6B7280]">Short:</span>{" "}
                            {shortenedUrl}
                          </div>
                        )}
                      </div>
                    )}

                    {loading && (
                      <p className="text-xs text-indigo-500 mt-1">Shortening URL...</p>
                    )}

                    {!isLinkValid && extractedUrl && (
                      <p className="text-xs text-red-500 mt-1">Please enter a valid URL</p>
                    )}
                  </div>

                  {/* Variable mapping — CSV Column / Static Value, same experience as WhatsApp */}
                  <VariableMappingForm mappings={variableMappings} onChange={setVariableMappings} />
                </div>

                {/* Footer actions */}
              </div>
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
                {/* <button
                  type="button"
                  className="px-5 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Save as Draft
                </button> */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onBack}
                    className="px-5 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                  >
                    Schedule and Launch
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* ── Right: Toggle Panel (Live Preview / ConvoAI) — desktop ── */}
          <div className="hidden lg:flex flex-col w-72 xl:w-[320px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <RightPanel
              messageText={previewText}
              senderName={campaignData?.sender_name}
              channel={campaignData?.campaign_channel}
              isConvoOpen={isConvoOpen}
              onToggleConvo={() => setIsConvoOpen(!isConvoOpen)}
              onConvoSend={handleConvoSend}
              costEstimate={costEstimate}
              smsSegments={smsMessageUnit}
            />
          </div>
        </div>
      </div>

      {/* ── Mobile FAB — opens Live Preview / ConvoAI drawer ── */}
      <button
        onClick={() => setIsMobilePreviewOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg shadow-indigo-200 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        aria-label="Open live preview"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 4V14H4a2 2 0 01-2-2V5z" />
        </svg>
      </button>

      {/* ── Mobile Drawer ── */}
      {isMobilePreviewOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMobilePreviewOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[80vh] bg-white dark:bg-gray-900 rounded-t-2xl overflow-hidden flex flex-col shadow-2xl">
            <div className="flex justify-end px-3 pt-2">
              <button
                onClick={() => setIsMobilePreviewOpen(false)}
                className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <RightPanel
                messageText={previewText}
                senderName={campaignData?.sender_name}
                channel={campaignData?.campaign_channel}
                isConvoOpen={isConvoOpen}
                onToggleConvo={() => setIsConvoOpen(!isConvoOpen)}
                onConvoSend={handleConvoSend}
                costEstimate={costEstimate}
                smsSegments={smsMessageUnit}
              />
            </div>
          </div>
        </div>
      )}
      <TemplateCreator
        isOpen={showCreator}
        templates={templates}
        onClose={() => setShowCreator(false)}
        onSaveDraft={(data) => {
          setShowCreator(false);
          fetchTemplates();
        }}
        onSendRequest={(data) => {
          setShowCreator(false);
          fetchTemplates();
        }}
        initialChannel="sms"
      />
      <ScrollToTop />
    </div>
  );
};

export default SmsContentCreation;